import React, { FC, memo, useCallback } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';

import {
  FarmsFiltersActions,
  FarmsFiltersType,
} from '@hooks/vaults/useFarmsList';

import Button from '@components/common/Button';
import FilterChains from '@components/common/FilterChains';
import FilterTabs from '@components/common/FilterTabs';
import Input from '@components/common/Input';
import Select from '@components/common/Select';
import SortButton from '@components/common/SortButton';

import { CHAIN_ID } from '@constants/common/chain.constants';
import { Size, Variant } from '@constants/common/ui.constants';
import {
  FARMS_FILTER_TABs,
  FARMS_SORT_BY,
} from '@constants/vaults/common.constants';

import useStyles from './styles.module';

interface Props {
  filters: FarmsFiltersType;
  actions: FarmsFiltersActions;
  selectedChains: CHAIN_ID[];
  onChangeChain: (chainId: number) => void;
  onDefaultChains?: () => void;
  className?: string;
}

const FarmsFilters: FC<Props> = ({
  filters,
  actions,
  selectedChains,
  onChangeChain,
  onDefaultChains,
  className,
}) => {
  const { classes, cx } = useStyles();

  const {
    search,
    sortBy,
    farmTypes,
    platforms,
    sortDirection,
    platformOptions,
    counterFilters,
  } = filters;

  const handleClearFilters = useCallback(() => {
    onDefaultChains?.();
    actions.onClearFilters();
  }, [actions, onDefaultChains]);

  return (
    <div className={classes.box}>
      <FilterChains
        selectedChains={selectedChains}
        onChangeChain={onChangeChain}
      />
      <div className={cx(classes.filtersBox, className)}>
        <Input
          isSearch
          value={search}
          onChange={(e) => actions.onSearch(e.target.value)}
          placeholder="Search"
          formClassName={classes.input}
        />
        <FilterTabs
          selectedValues={farmTypes}
          options={FARMS_FILTER_TABs}
          className={classes.filterTabs}
          onClick={actions.onToggleFarmTypes}
        />
        <div className={classes.selectBox}>
          <Select
            className={classes.select}
            placeholder="Sort by"
            value={sortBy}
            options={FARMS_SORT_BY}
            onChange={(e) => actions.onSortBy(e.target.value as string)}
          />
          <SortButton
            size={Size.Medium}
            type={sortDirection}
            onSort={actions.onSortDirection}
            className={classes.selectSortButton}
          />
        </div>
        <Select
          multiple
          className={classes.select}
          value={platforms}
          options={platformOptions}
          onChange={actions.onSetPlatform}
          placeholder="Platform"
        />
        <Button
          size={Size.Medium}
          variant={Variant.Clear}
          onClick={handleClearFilters}
          classNameChildren={classes.children}
          disabled={!counterFilters}
        >
          Clear
          {counterFilters ? (
            <Typography variant="caption1" className={classes.countFilter}>
              {counterFilters}
            </Typography>
          ) : (
            <CloseIcon className={classes.closeIcon} />
          )}
        </Button>
      </div>
    </div>
  );
};

export default memo(FarmsFilters);
